// apiFetch.ts (fonction pure)
import { resolveWorkflowConflict } from './workflowClient';

interface ApiError {
	code?: number;
	message?: string;
}

const isDev = process.env.NODE_ENV === 'development';

export interface ApiFetchOptions {
	// false : un 401 reste une erreur, sans redirection. Nécessaire avant la connexion.
	redirectOn401?: boolean;
}

export const apiFetch = async (
	input: RequestInfo,
	init?: RequestInit,
	options?: ApiFetchOptions
) => {
	// eslint-disable-next-line no-restricted-globals
	const response = await fetch(input, init);

	if (response.status === 403) {
		// Symfony peut renvoyer une exception HTML ou un JSON selon la config
		let forbiddenDetail: string | null = null;

		try {
			const contentType = response.headers.get('content-type') ?? '';
			if (contentType.includes('application/json')) {
				const json: ApiError = await response.clone().json();
				forbiddenDetail = json.message ?? null;
			}
			// Si HTML (ex: exception Symfony non gérée), on ne tente pas de parser
		} catch {
			// réponse non parseable
		}

		// En dev, on affiche le détail si disponible ; en prod, message générique
		const forbiddenMessage = isDev
			? (forbiddenDetail ?? 'Opération non permise détectée.')
			: 'Opération non permise détectée.';

		window.dispatchEvent(
			new CustomEvent('app:toast', {
				detail: { message: forbiddenMessage, type: 'error' },
			})
		);
		throw new Error(forbiddenMessage);
	}

	if (response.status === 401) {
		let error: ApiError | null = null;

		try {
			error = await response.clone().json();
		} catch {
			// réponse non JSON
		}

		if (options?.redirectOn401 !== false) {
			if (error?.message === 'Expired JWT Token') {
				localStorage.removeItem('token');
				window.location.href = '/login';
			} else {
				window.location.href = '/app/401';
			}
		}

		throw new Error(
			isDev ? (error?.message ?? 'Unauthorized') : 'Unauthorized'
		);
	}

	/*// 404 Not Found
	if (response.status === 404) {
		const text = isDev ? await response.text() : 'Ressource introuvable.';
		window.location.href = '/app/404'; // page 404 dédiée
		throw new Error(text || 'Not Found');
	}*/

	if (response.status === 400) {
		let message = 'Requête refusée.';
		try {
			const contentType = response.headers.get('content-type') ?? '';
			if (contentType.includes('application/json')) {
				const json: ApiError = await response.json();
				if (json.message) message = json.message;
			}
		} catch {
			// réponse non JSON
		}
		throw new Error(message);
	}

	if (response.status === 409) {
		const retried = await resolveWorkflowConflict(
			input,
			init,
			response,
			apiFetch
		);
		if (retried) {
			return retried;
		}
	}

	if (!response.ok) {
		const text = isDev ? await response.text() : 'Une erreur est survenue.';
		throw new Error(text || response.statusText);
	}

	return response;
};

export default apiFetch;
